import React, { useEffect } from 'react';
import { TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon, DownloadIcon, InfoIcon } from 'lucide-react';
interface LimitRow {
  label: string;
  value: string;
  status: 'pass' | 'warn' | 'watch';
  note?: string;
}
const DEMO = {
  totalFunded: 50000000,
  outstanding: 38400000,
  avgDaysToPay: 42.3,
  currentYield: 14.7,
  next30dCashflow: 12800000,
  portfolioHealth: 96.2,
  onTimeRate: 96,
  disputeRatio: 2.1
};
const PORTFOLIO_ROWS = [{
  id: 'INV-8831',
  amount: 124500,
  anchor: 'Premium Textiles',
  payor: 'Target Corp',
  authScore: 98.3,
  payorScore: 96.5,
  verdict: 'APPROVED',
  advance: 85,
  yield: 14.2,
  predicted: '2025-02-15',
  status: 'FUNDED'
}, {
  id: 'INV-8722',
  amount: 92440,
  anchor: 'Global Apparel',
  payor: 'Walmart',
  authScore: 99.1,
  payorScore: 98.2,
  verdict: 'APPROVED',
  advance: 90,
  yield: 14.1,
  predicted: '2025-02-10',
  status: 'SETTLED'
}, {
  id: 'INV-8645',
  amount: 156800,
  anchor: 'Fashion Forward',
  payor: 'HomeGoods',
  authScore: 97.8,
  payorScore: 95.1,
  verdict: 'APPROVED',
  advance: 82,
  yield: 15.1,
  predicted: '2025-02-20',
  status: 'FUNDED'
}, {
  id: 'INV-8512',
  amount: 203400,
  anchor: 'Metro Fabric',
  payor: 'Target Corp',
  authScore: 98.9,
  payorScore: 96.5,
  verdict: 'APPROVED',
  advance: 88,
  yield: 13.5,
  predicted: '2025-02-18',
  status: 'FUNDED'
}];
export function LiquidityDesk() {
  const formatUSD = (n: number) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${n.toLocaleString()}`;
  useEffect(() => {
    document.title = 'Mint | Portfolio Intelligence';
  }, []);
  return <div className="min-h-screen bg-gray-50" data-id="element-1697">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between" data-id="element-1698">
        <div data-id="element-1699">
          <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1700">
            Portfolio Intelligence
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-id="element-1701">
            View of funded receivables
          </p>
        </div>
        <button className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50" data-id="element-1702">
          <DownloadIcon size={14} className="mr-1" data-id="element-1703" />
          Export CSV
        </button>
      </div>
      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" data-id="element-1704">
        <TopTile label="Total funded" value={formatUSD(DEMO.totalFunded)} data-id="element-1705" />
        <TopTile label="Outstanding" value={formatUSD(DEMO.outstanding)} data-id="element-1706" />
        <TopTile label="Average days to pay" value={`${DEMO.avgDaysToPay.toFixed(1)}d`} data-id="element-1707" />
        <TopTile label="Current yield" value={`${DEMO.currentYield.toFixed(1)}%`} data-id="element-1708" />
        <TopTile label="Next 30D cashflow" value={formatUSD(DEMO.next30dCashflow)} data-id="element-1709" />
        <TopTile label="Portfolio health" value={`${DEMO.portfolioHealth.toFixed(1)}%`} data-id="element-1710" />
      </div>
      {/* Main grid: left – performance, right – risk snapshot */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8" data-id="element-1711">
        {/* Left column */}
        <div className="xl:col-span-2 space-y-6" data-id="element-1712">
          {/* Performance panel */}
          <CardPanel title="Cashflow and performance" data-id="element-1713">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1714">
              {/* Aging block */}
              <div data-id="element-1715">
                <div className="text-xs text-gray-500 uppercase mb-2" data-id="element-1716">
                  On-time payment rate
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-1717">
                  {DEMO.onTimeRate.toFixed(1)}%
                </div>
                <div className="space-y-2" data-id="element-1718">
                  <AgingRow label="Current" value={72} data-id="element-1719" />
                  <AgingRow label="0–30 days" value={18} data-id="element-1720" />
                  <AgingRow label="30–60 days" value={7} data-id="element-1721" />
                  <AgingRow label="60–90 days" value={2} data-id="element-1722" />
                  <AgingRow label="90+ days" value={1} data-id="element-1723" />
                </div>
              </div>
              {/* Dispute block */}
              <div data-id="element-1724">
                <div className="text-xs text-gray-500 uppercase mb-2" data-id="element-1725">
                  Disputes and dilution
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-1726">
                  {DEMO.disputeRatio.toFixed(1)}%
                  <span className="ml-2 text-xs text-gray-500 font-normal" data-id="element-1727">
                    disputed value
                  </span>
                </div>
                <div className="space-y-2 text-xs" data-id="element-1728">
                  <MetricBarRow label="Quality issues" value={45} data-id="element-1729" />
                  <MetricBarRow label="Quantity mismatch" value={30} data-id="element-1730" />
                  <MetricBarRow label="Pricing error" value={25} data-id="element-1731" />
                </div>
                <div className="mt-3 text-xs text-gray-600 flex items-center" data-id="element-1732">
                  <CheckCircleIcon size={12} className="text-emerald-600 mr-1" data-id="element-1733" />
                  Within internal tolerance band
                </div>
              </div>
            </div>
          </CardPanel>
          {/* Portfolio drill-down table */}
          <CardPanel title="Portfolio drill-down" data-id="element-1734">
            <div className="overflow-x-auto" data-id="element-1735">
              <table className="min-w-full text-xs" data-id="element-1736">
                <thead className="bg-gray-50 text-gray-500 uppercase" data-id="element-1737">
                  <tr data-id="element-1738">
                    <th className="px-4 py-2 text-left" data-id="element-1739">Invoice ID</th>
                    <th className="px-4 py-2 text-left" data-id="element-1740">Amount</th>
                    <th className="px-4 py-2 text-left" data-id="element-1741">Anchor</th>
                    <th className="px-4 py-2 text-left" data-id="element-1742">Payor</th>
                    <th className="px-4 py-2 text-left" data-id="element-1743">Auth score</th>
                    <th className="px-4 py-2 text-left" data-id="element-1744">Payor score</th>
                    <th className="px-4 py-2 text-left" data-id="element-1745">Verdict</th>
                    <th className="px-4 py-2 text-left" data-id="element-1746">Advance</th>
                    <th className="px-4 py-2 text-left" data-id="element-1747">Yield</th>
                    <th className="px-4 py-2 text-left" data-id="element-1748">Predicted</th>
                    <th className="px-4 py-2 text-left" data-id="element-1749">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1750">
                  {PORTFOLIO_ROWS.map(row => <tr key={row.id} className="hover:bg-gray-50" data-id="element-1751">
                      <td className="px-4 py-3 text-indigo-600 font-medium" data-id="element-1752">
                        {row.id}
                      </td>
                      <td className="px-4 py-3" data-id="element-1753">
                        ${row.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-id="element-1754">{row.anchor}</td>
                      <td className="px-4 py-3 text-gray-700" data-id="element-1755">{row.payor}</td>
                      <td className="px-4 py-3 text-gray-900" data-id="element-1756">
                        {row.authScore}
                      </td>
                      <td className="px-4 py-3 text-gray-900" data-id="element-1757">
                        {row.payorScore}
                      </td>
                      <td className="px-4 py-3" data-id="element-1758">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800" data-id="element-1759">
                          {row.verdict}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-id="element-1760">
                        {row.advance}%
                      </td>
                      <td className="px-4 py-3 text-gray-900" data-id="element-1761">{row.yield}%</td>
                      <td className="px-4 py-3 text-gray-600" data-id="element-1762">
                        {row.predicted}
                      </td>
                      <td className="px-4 py-3" data-id="element-1763">
                        <StatusChip status={row.status} data-id="element-1764" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardPanel>
        </div>
        {/* Right column */}
        <div className="space-y-6" data-id="element-1765">
          <CardPanel title="Risk snapshot" data-id="element-1766">
            <div className="space-y-3 text-sm" data-id="element-1767">
              <RowLabel label="Overall status" value="Compliant" icon={<CheckCircleIcon size={14} className="text-emerald-600" data-id="element-1769" />} data-id="element-1768" />
              <RowLabel label="Concentration" value="Top obligor 18.2% (limit 20%)" icon={<AlertTriangleIcon size={14} className="text-amber-500" data-id="element-1771" />} data-id="element-1770" />
              <RowLabel label="Sector mix" value="Retail 31.0% (limit 35%)" icon={<TrendingUpIcon size={14} className="text-gray-500" data-id="element-1773" />} data-id="element-1772" />
            </div>
          </CardPanel>
          <CardPanel title="Covenant limits" data-id="element-1774">
            <div className="space-y-3 text-sm" data-id="element-1775">
              <LimitRowItem row={{
              label: 'Obligor concentration < 20%',
              value: '18.2%',
              status: 'warn'
            }} data-id="element-1776" />
              <LimitRowItem row={{
              label: 'Top 5 concentration < 60%',
              value: '52.8%',
              status: 'watch'
            }} data-id="element-1777" />
              <LimitRowItem row={{
              label: 'Average tenor < 60 days',
              value: '42.3d',
              status: 'pass'
            }} data-id="element-1778" />
            </div>
          </CardPanel>
          <CardPanel title="Recent alerts" data-id="element-1779">
            <div className="space-y-2 text-xs" data-id="element-1780">
              <AlertRow time="09:42" kind="warn" text="Walmart exposure drifting toward 20% cap" data-id="element-1781" />
              <AlertRow time="09:31" kind="info" text="AI model highlighted dispute uptick for Anchor D (7D window)" data-id="element-1782" />
              <AlertRow time="09:14" kind="info" text="Collection rate rolling 30D improved to 98.7%" data-id="element-1783" />
            </div>
          </CardPanel>
        </div>
      </div>
    </div>;
}
/* Helper components */
function TopTile({
  label,
  value,
  highlight
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1784">
      <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-1" data-id="element-1785">
        {label}
      </div>
      <div className={`text-xl font-semibold ${highlight ? 'text-emerald-600' : 'text-gray-900'}`} data-id="element-1786">
        {value}
      </div>
    </div>;
}
function CardPanel({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1787">
      <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1788">
        {title}
      </div>
      {children}
    </div>;
}
function AgingRow({
  label,
  value
}: {
  label: string;
  value: number;
}) {
  return <div className="mb-2" data-id="element-1789">
      <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-1790">
        <span className="text-gray-600" data-id="element-1791">{label}</span>
        <span className="text-gray-800 font-medium" data-id="element-1792">{value}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1793">
        <div className="h-full bg-gray-700" style={{
        width: `${value}%`
      }} data-id="element-1794"></div>
      </div>
    </div>;
}
function MetricBarRow({
  label,
  value
}: {
  label: string;
  value: number;
}) {
  return <div data-id="element-1795">
      <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-1796">
        <span className="text-gray-600" data-id="element-1797">{label}</span>
        <span className="text-gray-800 font-medium" data-id="element-1798">{value}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1799">
        <div className="h-full bg-gray-500" style={{
        width: `${value}%`
      }} data-id="element-1800"></div>
      </div>
    </div>;
}
function StatusChip({
  status
}: {
  status: string;
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (status === 'SETTLED') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1801">{status}</span>;
  }
  return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1802">{status}</span>;
}
function RowLabel({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return <div className="flex items-start justify-between" data-id="element-1803">
      <div className="text-xs text-gray-600" data-id="element-1804">{label}</div>
      <div className="flex items-center text-xs text-gray-900" data-id="element-1805">
        {icon && <span className="mr-1" data-id="element-1806">{icon}</span>}
        <span data-id="element-1807">{value}</span>
      </div>
    </div>;
}
function LimitRowItem({
  row
}: {
  row: LimitRow;
}) {
  return <div className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2 bg-gray-50" data-id="element-1808">
      <div className="flex-1" data-id="element-1809">
        <div className="text-xs text-gray-600" data-id="element-1810">{row.label}</div>
        {row.note && <div className="text-[11px] text-gray-500 mt-1" data-id="element-1811">{row.note}</div>}
      </div>
      <div className="flex items-center gap-2" data-id="element-1812">
        <span className="text-sm font-semibold text-gray-900" data-id="element-1813">{row.value}</span>
        <LimitStatus status={row.status} data-id="element-1814" />
      </div>
    </div>;
}
function LimitStatus({
  status
}: {
  status: 'pass' | 'warn' | 'watch';
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (status === 'pass') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1815">Pass</span>;
  }
  if (status === 'warn') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1816">Warn</span>;
  }
  return <span className={`${base} bg-gray-900 text-white`} data-id="element-1817">Watch</span>;
}
function AlertRow({
  time,
  kind,
  text
}: {
  time: string;
  kind: 'info' | 'warn' | 'breach';
  text: string;
}) {
  const icon = kind === 'breach' ? <AlertTriangleIcon size={12} className="text-red-600" data-id="element-1818" /> : kind === 'warn' ? <AlertTriangleIcon size={12} className="text-amber-500" data-id="element-1819" /> : <InfoIcon size={12} className="text-blue-500" data-id="element-1820" />;
  return <div className="flex items-start gap-2 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0" data-id="element-1821">
      <span className="text-gray-500 w-10" data-id="element-1822">{time}</span>
      <span data-id="element-1823">{icon}</span>
      <span className="text-gray-800" data-id="element-1824">{text}</span>
    </div>;
}